"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useCookieConsent } from "@components/layout/CookieConsent";

export interface StickyPriceBarData {
  text: string;
  ctaText: string;
  ctaHref: string;
}

interface StickyPriceBarProps {
  data: StickyPriceBarData;
  /** Ref to the element that, when scrolled past, reveals the bar */
  sentinelRef?: React.RefObject<HTMLElement | null>;
}

export default function StickyPriceBar({
  data,
  sentinelRef,
}: StickyPriceBarProps) {
  const [scrolledPast, setScrolledPast] = useState(false);
  const isCalendlyLink = data.ctaHref.includes("calendly.com");
  const defaultSentinelRef = useRef<HTMLDivElement>(null);
  const { isVisible: cookieBannerVisible } = useCookieConsent();

  useEffect(() => {
    const target = sentinelRef?.current ?? defaultSentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrolledPast(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [sentinelRef]);

  // Hide whenever the cookie banner is open (initial consent prompt or
  // re-expanded via the mini icon) so the two never overlap.
  const visible = scrolledPast && !cookieBannerVisible;

  return (
    <>
      {/* Default sentinel placed at document root if no external ref provided */}
      {!sentinelRef && (
        <div
          ref={defaultSentinelRef}
          aria-hidden="true"
          className="sticky-bar-sentinel"
        />
      )}

      <div
        role="complementary"
        aria-label="Schnellzugriff Preisrechner"
        className={[
          "fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out",
          visible ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
      >
        <div className="bg-abis-primary border-t border-white/10 shadow-[0_-4px_24px_rgba(0,0,0,0.25)]">
          <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 max-w-5xl">
            <p className="text-white/90 text-sm font-medium text-center sm:text-left">
              {data.text}
            </p>
            <Link
              href={data.ctaHref}
              target={isCalendlyLink ? "_blank" : undefined}
              rel={isCalendlyLink ? "nofollow noopener noreferrer" : undefined}
              className="flex-shrink-0 flex items-center gap-2 bg-abis-accent hover:bg-abis-accent/90 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-abis-primary"
              aria-label={`${data.ctaText} - Zum Preisrechner springen`}
            >
              {data.ctaText}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
